import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import type { User } from "@/lib/types";

interface AuthValue {
  user: User | null | false;
  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue>(null as any);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null | false>(null);

  const check = useCallback(async () => {
    try {
      setUser((await api.get<User>("/auth/me")).data);
    } catch {
      try {
        setUser((await api.post<User>("/auth/refresh")).data);
      } catch {
        setUser(false);
      }
    }
  }, []);

  useEffect(() => {
    check();
    const onExpired = () => setUser(false);
    window.addEventListener("auth:expired", onExpired);
    return () => window.removeEventListener("auth:expired", onExpired);
  }, [check]);

  const login = async (email: string, password: string) => {
    const { data } = await api.post<User>("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => null);
    setUser(false);
  };

  return <AuthContext.Provider value={{ user, login, logout }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);

export function homeFor(user: User): string {
  if (user.role === "super_admin") return "/admin/organisations";
  if (user.role === "editor") return "/medias";
  return "/tableau-de-bord";
}
